/**
 * The traceability links added and removed between two revisions.
 *
 * Grouped by relationship, because a reader checking whether the award
 * standard coverage moved is not also checking assessment links.
 *
 * @module components/portfolio/comparison/MappingChangeList
 */

import React, { useState } from "react";

import type {
  MappingDifference,
  MappingRelationship,
} from "@/components/portfolio/comparison/diff-types";
import {
  Accordion,
  AccordionItem,
  Alert,
  Badge,
  EmptyState,
  Eyebrow,
  FILTER_CONTROL_ROW,
  FilterPanel,
  FormField,
  Heading,
  Icon,
  MutedText,
  SelectField,
  Text,
  TextField,
} from "@/components/ui";

/** What each end of a relationship is called, so a link reads as a sentence. */
const MAPPING_ENDPOINT_LABELS: Record<MappingRelationship, { source: string; target: string }> = {
  "Award standard to programme outcome": {
    source: "Award standard indicator",
    target: "Programme learning outcome",
  },
  "Programme outcome to module outcome": {
    source: "Programme learning outcome",
    target: "Module learning outcome",
  },
  "Module outcome to assessment": {
    source: "Module learning outcome",
    target: "Assessment",
  },
};

export interface MappingChangeListProps {
  mappings: readonly MappingDifference[];
}

/** One changed link, with both endpoints named. */
function MappingRow({ mapping }: { mapping: MappingDifference }): React.ReactElement {
  const endpoints = MAPPING_ENDPOINT_LABELS[mapping.relationship];
  return (
    <Alert
      tone={mapping.status === "added" ? "success" : "error"}
      // One alert per changed link would be a screen of live-region
      // announcements; the Added or Removed badge carries the meaning.
      role="none"
      className="[&>div]:w-full"
    >
      <Badge size="sm" tone={mapping.status === "added" ? "success" : "error"}>
        {mapping.status === "added" ? "Added link" : "Removed link"}
      </Badge>
      <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
        <div className="min-w-0">
          <Eyebrow as="div">{endpoints.source}</Eyebrow>
          <div className="mt-1 text-md font-medium">{mapping.sourceLabel}</div>
          {mapping.sourceContext ? (
            <MutedText size="caption" className="mt-1">
              {mapping.sourceContext}
            </MutedText>
          ) : null}
        </div>
        <Text
          as="div"
          size="sm"
          className="flex items-center gap-2 font-semibold text-primary-strong"
        >
          <span>Maps to</span>
          <Icon name="arrow-right" aria-hidden />
        </Text>
        <div className="min-w-0">
          <Eyebrow as="div">{endpoints.target}</Eyebrow>
          <div className="mt-1 text-md font-medium">{mapping.targetLabel}</div>
          {mapping.context ? (
            <MutedText size="caption" className="mt-1">
              {mapping.context}
            </MutedText>
          ) : null}
        </div>
      </div>
    </Alert>
  );
}

/**
 * @example
 * ```tsx
 * <MappingChangeList mappings={compareMappings(before, after)} />
 * ```
 */
export function MappingChangeList({ mappings }: MappingChangeListProps): React.ReactElement {
  const [statusFilter, setStatusFilter] = useState("all");
  const [relationshipFilter, setRelationshipFilter] = useState("all");
  const [search, setSearch] = useState("");
  const normalisedSearch = search.trim().toLocaleLowerCase("en-IE");
  const visible = mappings.filter(
    (mapping) =>
      (statusFilter === "all" || mapping.status === statusFilter) &&
      (relationshipFilter === "all" || mapping.relationship === relationshipFilter) &&
      (normalisedSearch.length === 0 ||
        [mapping.sourceLabel, mapping.sourceContext, mapping.targetLabel, mapping.context]
          .filter((value): value is string => value !== undefined)
          .some((value) => value.toLocaleLowerCase("en-IE").includes(normalisedSearch))),
  );
  const grouped = visible.reduce<Map<MappingRelationship, MappingDifference[]>>(
    (result, mapping) => {
      result.set(mapping.relationship, [...(result.get(mapping.relationship) ?? []), mapping]);
      return result;
    },
    new Map(),
  );

  return (
    <section aria-labelledby="mapping-changes-heading">
      <Heading level={2} id="mapping-changes-heading">
        Traceability mapping changes
      </Heading>
      <MutedText className="mt-1">
        Shows links added or removed across award standards, outcomes, and assessments.
      </MutedText>
      {mappings.length === 0 ? (
        <EmptyState
          className="mt-6"
          icon="share-network"
          title="No mapping relationships changed between these revisions"
          density="row"
          data-testid="comparison-mappings-empty"
        />
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge tone="success">
              {mappings.filter((mapping) => mapping.status === "added").length} added
            </Badge>
            <Badge tone="error">
              {mappings.filter((mapping) => mapping.status === "removed").length} removed
            </Badge>
          </div>
          <div className="mt-5">
            <FilterPanel
              filtering={{
                label: "Filter mapping changes",
                count: `${visible.length} of ${mappings.length} changed links shown`,
                // From the controls, not from what they match, as
                // `useCollectionFilters` is: a search of only spaces narrows
                // nothing and still fills the field.
                active: search.length > 0 || statusFilter !== "all" || relationshipFilter !== "all",
                defaultOpen: true,
                onClear: () => {
                  setSearch("");
                  setStatusFilter("all");
                  setRelationshipFilter("all");
                },
                controls: (
                  <div className={FILTER_CONTROL_ROW}>
                    <FormField label="Find a mapping" htmlFor="mapping-comparison-search">
                      <TextField
                        id="mapping-comparison-search"
                        value={search}
                        onChange={setSearch}
                        placeholder="Outcome, assessment, or module"
                      />
                    </FormField>
                    <FormField label="Change" htmlFor="mapping-comparison-status">
                      <SelectField
                        id="mapping-comparison-status"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                          { value: "all", label: "Added and removed" },
                          { value: "added", label: "Added" },
                          { value: "removed", label: "Removed" },
                        ]}
                      />
                    </FormField>
                    <FormField label="Relationship" htmlFor="mapping-comparison-relationship">
                      <SelectField
                        id="mapping-comparison-relationship"
                        value={relationshipFilter}
                        onChange={setRelationshipFilter}
                        options={[
                          { value: "all", label: "All relationships" },
                          ...[...new Set(mappings.map((mapping) => mapping.relationship))].map(
                            (relationship) => ({ value: relationship, label: relationship }),
                          ),
                        ]}
                      />
                    </FormField>
                  </div>
                ),
              }}
            />
          </div>
          {visible.length === 0 ? (
            <EmptyState
              className="mt-5"
              icon="funnel"
              title="No mapping changes match these filters"
              description="Clear a filter to see the rest of the changed links."
              density="row"
              data-testid="comparison-mappings-filtered-empty"
            />
          ) : (
            <Accordion id="comparison-mappings" className="mt-5 space-y-4">
              {[...grouped.entries()].map(([relationship, entries]) => (
                <AccordionItem
                  key={relationship}
                  eventKey={`comparison-mapping-group-${relationship}`}
                  headingLevel={3}
                  title={relationship}
                  subtitle={`${entries.length} ${entries.length === 1 ? "change" : "changes"}. Select to review affected links.`}
                  data-testid={`comparison-mapping-group-${relationship}`}
                >
                  <div className="space-y-3">
                    {entries.map((mapping) => (
                      <MappingRow key={`${mapping.status}:${mapping.id}`} mapping={mapping} />
                    ))}
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </>
      )}
    </section>
  );
}
